import React, { useState } from 'react';
import { ArrowRight, Clock, ShieldCheck, Camera, CheckCircle2, Video, MapPin, Brain } from 'lucide-react';
import { LandingBackgroundMedia } from '../components/landing/LandingBackgroundMedia';
import LandingHeroSection from '../components/landing/LandingHeroSection';
import LandingFinalCtaSection from '../components/landing/LandingFinalCtaSection';
import LandingFooterSection from '../components/landing/LandingFooterSection';
import PublicHeader from '../components/PublicHeader';
import ConsultingRequestModal from '../components/ConsultingRequestModal';
import { getVerticalBranding, getVerticalLandingAssets, type VerticalType } from '../config/vertical';
import { LANDING_CONTENT } from '../config/landing_content';

/** Base path for public assets (supports subpath deployment). */
const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
/** Default paths when env vars are not set — put files in frontend/public/landing/ */
const HERO_VIDEO_FALLBACK = process.env.REACT_APP_LANDING_HERO_VIDEO ?? `${publicUrl}/landing/flyover.mp4`;
const HERO_POSTER = process.env.REACT_APP_LANDING_HERO_POSTER;
const GRAPES_VIDEO_FALLBACK = process.env.REACT_APP_LANDING_GRAPES_VIDEO ?? `${publicUrl}/landing/grapes.mp4`;
const GRAPES_POSTER = process.env.REACT_APP_LANDING_GRAPES_POSTER;
const CONSULTING_BG_FALLBACK = process.env.REACT_APP_LANDING_CONSULTING_BG ?? `${publicUrl}/landing/consulting-bg.jpg`;
const GREENHOUSE_HERO_IMAGE_FALLBACK = process.env.REACT_APP_GREENHOUSE_LANDING_HERO_IMAGE ?? `${publicUrl}/landing/greenhouse-hero.jpg`;
const GREENHOUSE_GRAPES_IMAGE_FALLBACK = process.env.REACT_APP_GREENHOUSE_LANDING_GRAPES_IMAGE ?? `${publicUrl}/landing/greenhouse-zones.jpg`;
const ORCHARD_HERO_IMAGE_FALLBACK = process.env.REACT_APP_ORCHARD_LANDING_HERO_IMAGE ?? `${publicUrl}/landing/orchard-hero.jpg`;
const ORCHARD_GRAPES_IMAGE_FALLBACK = process.env.REACT_APP_ORCHARD_LANDING_GRAPES_IMAGE ?? `${publicUrl}/landing/orchard-rows.jpg`;

interface LandingProps {
  vertical?: VerticalType;
}

const Landing: React.FC<LandingProps> = ({ vertical = 'vineyard' }) => {
  const [consultingModalOpen, setConsultingModalOpen] = useState(false);
  const { brandName: headerBrandName, logoSrc: headerLogoSrc } = getVerticalBranding(vertical);
  const landingAssets = getVerticalLandingAssets(vertical);
  const assetPath = (path?: string) => (path ? `${publicUrl}${path}` : undefined);
  const heroVideo = assetPath(landingAssets?.hero_video) || HERO_VIDEO_FALLBACK;
  const detailVideo = assetPath(landingAssets?.detail_video) || GRAPES_VIDEO_FALLBACK;
  const heroImage = assetPath(landingAssets?.hero_image);
  const detailImage = assetPath(landingAssets?.detail_image);
  const consultingBg = assetPath(landingAssets?.consulting_bg) || CONSULTING_BG_FALLBACK;
  const content = LANDING_CONTENT[vertical];
  const unitLabel = vertical === 'greenhouse' ? 'zone' : 'block';
  const unitLabelPlural = vertical === 'greenhouse' ? 'zones' : 'blocks';
  const domainLabel = vertical === 'vineyard' ? 'vineyard' : vertical;
  const isVineyard = vertical === 'vineyard';
  const isOrchard = vertical === 'orchard';
  const isGreenhouse = vertical === 'greenhouse';
  const accentBg = isVineyard ? 'bg-vineyard-light' : isOrchard ? 'bg-blue-50' : 'bg-green-50';
  const primaryIconBg = isVineyard ? 'bg-grape-100' : isOrchard ? 'bg-blue-100' : 'bg-green-100';
  const primaryIconText = isVineyard ? 'text-grape-600' : isOrchard ? 'text-blue-600' : 'text-green-600';
  const accentBorder = isVineyard ? 'border-grape-200' : isOrchard ? 'border-blue-200' : 'border-green-200';
  const accentBorderLeft = isVineyard ? 'border-l-grape-600' : isOrchard ? 'border-l-blue-600' : 'border-l-green-600';
  const accentRing = isVineyard ? 'ring-grape-100/50' : isOrchard ? 'ring-blue-100/50' : 'ring-green-100/50';
  const finalCtaBg = isVineyard ? 'bg-grape-700' : isOrchard ? 'bg-blue-700' : 'bg-green-700';
  const finalCtaButton = isVineyard ? 'bg-grape-600 hover:bg-grape-700' : isOrchard ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700';
  const sectionEntityTitle = isVineyard ? 'Vineyard' : isOrchard ? 'Orchard' : 'Greenhouse';
  const footerClass = isGreenhouse ? 'bg-leaf-950 border-t-2 border-leaf-500' : 'bg-wood-800';
  const footerTextClass = isGreenhouse ? 'text-leaf-200 hover:text-white' : 'text-wood-300 hover:text-vineyard-white';
  const footerMutedClass = isGreenhouse ? 'text-leaf-400' : 'text-wood-400';
  const appDataLabel = content.appDataLabel;
  const heroTitle = content.heroTitle;

  return (
    <div className={`min-h-screen ${accentBg}`}>
      <PublicHeader vertical={vertical} brandName={headerBrandName} logoSrc={headerLogoSrc} />

      <LandingHeroSection
        vertical={vertical}
        heroTitle={heroTitle}
        unitLabel={unitLabel}
        sectionEntityTitle={sectionEntityTitle}
        heroVideo={heroVideo}
        heroPoster={HERO_POSTER}
        heroImage={heroImage}
        isGreenhouse={isGreenhouse}
        isOrchard={isOrchard}
        greenhouseHeroFallback={GREENHOUSE_HERO_IMAGE_FALLBACK}
        orchardHeroFallback={ORCHARD_HERO_IMAGE_FALLBACK}
      />

      {/* Three Focus Points */}
      <section className={`py-8 sm:py-10 ${accentBg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-wood-900 mb-2">
              Everything you need, organized by {unitLabel}
            </h2>
            <p className="text-lg text-wood-700 max-w-2xl mx-auto">
              Three core workflows designed for practical {unitLabel}-level management
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {/* Focus Point 1: Labor Tracking */}
            <div className={`bg-white rounded-lg shadow-lg border-2 ${accentBorder} p-3 sm:p-4 hover:shadow-xl transition-shadow`}>
              <div className={`flex items-center justify-center w-16 h-16 ${primaryIconBg} rounded-full mb-3`}>
                <Clock className={`h-8 w-8 ${primaryIconText}`} />
              </div>
              <h3 className="text-2xl font-bold text-wood-900 mb-2">
                {unitLabel.charAt(0).toUpperCase() + unitLabel.slice(1)}-Level Labor Tracking
              </h3>
              <p className="text-wood-700 mb-3">
                See exactly which {unitLabelPlural} cost the most in labor — and which are profitable. Schedule tasks by {unitLabel}, track time in the field, and automatically calculate true labor cost per {unitLabel}.
              </p>
              <ul className="space-y-2 text-wood-800 text-sm">
                <li className="flex items-start">
                  <CheckCircle2 className={`h-5 w-5 ${primaryIconText} mr-2 flex-shrink-0 mt-0.5`} />
                  <span>Schedule tasks by {unitLabel}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className={`h-5 w-5 ${primaryIconText} mr-2 flex-shrink-0 mt-0.5`} />
                  <span>Crews log time directly to {unitLabelPlural}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className={`h-5 w-5 ${primaryIconText} mr-2 flex-shrink-0 mt-0.5`} />
                  <span>Automatic cost per {unitLabel}, per acre</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className={`h-5 w-5 ${primaryIconText} mr-2 flex-shrink-0 mt-0.5`} />
                  <span>Track trends over time</span>
                </li>
              </ul>
            </div>

            {/* Focus Point 2: Compliance & Spray Management */}
            <div className={`bg-white rounded-lg shadow-lg border-2 ${accentBorder} p-3 sm:p-4 hover:shadow-xl transition-shadow`}>
              <div className="flex items-center justify-center w-16 h-16 bg-leaf-100 rounded-full mb-3">
                <ShieldCheck className="h-8 w-8 text-leaf-600" />
              </div>
              <h3 className="text-2xl font-bold text-wood-900 mb-2">
                Compliance & Spray Management
              </h3>
              <p className="text-wood-700 mb-3">
                Zero-touch compliance tracking. Log sprays and the system automatically calculates REI/PHI windows, checks harvest safety, and validates tank mix compatibility.
              </p>
              <ul className="space-y-2 text-wood-800 text-sm">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-leaf-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Automatic REI/PHI calculation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-leaf-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>MI Calculator for tank mix compatibility</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-leaf-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Harvest safety warnings</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-leaf-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Auto-created compliance records</span>
                </li>
              </ul>
            </div>

            {/* Focus Point 3: Scouting & Field Management */}
            <div className={`bg-white rounded-lg shadow-lg border-2 ${accentBorder} p-3 sm:p-4 hover:shadow-xl transition-shadow`}>
              <div className="flex items-center justify-center w-16 h-16 bg-wood-100 rounded-full mb-3">
                <Camera className="h-8 w-8 text-wood-600" />
              </div>
              <h3 className="text-2xl font-bold text-wood-900 mb-2">
                Scouting & Field Management
              </h3>
              <p className="text-wood-700 mb-3">
                Document issues with photos, track severity, integrate sensor data, and let the system automatically create follow-up tasks. Never lose track of what needs attention.
              </p>
              <ul className="space-y-2 text-wood-800 text-sm">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-wood-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Photo documentation per scouting record</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-wood-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Integrate weather, soil, and sensor data by {unitLabel}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-wood-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Automatic follow-up task creation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-wood-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Track severity and urgency</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-wood-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{unitLabel.charAt(0).toUpperCase() + unitLabel.slice(1)}-level issue tracking</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works — media background */}
      <section className="relative py-14 sm:py-16 bg-white min-h-[24rem]">
        {isGreenhouse ? (
          <LandingBackgroundMedia
            fallbackSrc={detailImage || GREENHOUSE_GRAPES_IMAGE_FALLBACK}
            alt="Greenhouse zones close-up"
            overlay="subtle"
            lazy={true}
          />
        ) : isOrchard ? (
          <LandingBackgroundMedia
            fallbackSrc={detailImage || ORCHARD_GRAPES_IMAGE_FALLBACK}
            alt="Orchard rows close-up"
            overlay="subtle"
            lazy={true}
          />
        ) : (
          <LandingBackgroundMedia
            videoSrc={detailVideo}
            posterSrc={GRAPES_POSTER}
            alt={`${sectionEntityTitle} detail`}
            overlay="subtle"
            lazy={true}
          />
        )}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-wood-900 mb-8 text-center">
            <span className={`inline-block px-4 py-2 rounded-xl bg-white/80 backdrop-blur-sm border-2 ${accentBorder} shadow-lg ring-1 ${accentRing} text-wood-900`}>
              How it works
            </span>
          </h2>
          <div className="space-y-6">
            <div className={`bg-white/80 backdrop-blur-sm p-6 rounded-xl border-2 ${accentBorder} border-l-4 ${accentBorderLeft} shadow-lg`}>
              <h3 className="font-bold text-wood-900 mb-2">1. Schedule work by {unitLabel}</h3>
              <p className="text-wood-700">
                Assign tasks to specific {unitLabelPlural}. Crews log time directly to those {unitLabelPlural} when they start work.
              </p>
            </div>
            <div className={`bg-white/80 backdrop-blur-sm p-6 rounded-xl border-2 ${accentBorder} border-l-4 ${accentBorderLeft} shadow-lg ring-1 ${accentRing}`}>
              <h3 className="font-bold text-wood-900 mb-2">2. Track everything in one place</h3>
              <p className="text-wood-700">
                Log sprays, scout issues, record harvests, and integrate sensor data — all tied to {unitLabelPlural}. The system handles compliance calculations and follow-ups automatically.
              </p>
            </div>
            <div className={`bg-white/80 backdrop-blur-sm p-6 rounded-xl border-2 ${accentBorder} border-l-4 ${accentBorderLeft} shadow-lg ring-1 ${accentRing}`}>
              <h3 className="font-bold text-wood-900 mb-2">3. See {unitLabel}-level insights</h3>
              <p className="text-wood-700">
                View labor costs, compliance status, and field conditions per {unitLabel}. Identify which {unitLabelPlural} are profitable and which need attention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For + Pricing (combined) */}
      <section className={`py-14 sm:py-16 ${accentBg}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
            {/* Who this is for */}
            <div className="flex flex-col min-h-0">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-wood-900 mb-6 text-center lg:text-left">
                Who this is for
              </h2>
              <div className="flex flex-col gap-6 flex-1 min-h-0">
                <div className={`bg-white p-6 rounded-lg border-2 ${accentBorder} shadow-md`}>
                  <h3 className="text-xl font-bold text-wood-900 mb-3">Good fit</h3>
                  <ul className="space-y-2 text-wood-800">
                    <li className="flex items-start">
                      <CheckCircle2 className={`h-5 w-5 ${primaryIconText} mr-2 flex-shrink-0 mt-0.5`} />
                      <span>{sectionEntityTitle}s managing multiple {unitLabelPlural}</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className={`h-5 w-5 ${primaryIconText} mr-2 flex-shrink-0 mt-0.5`} />
                      <span>Operations tracking labor, compliance, or field conditions</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className={`h-5 w-5 ${primaryIconText} mr-2 flex-shrink-0 mt-0.5`} />
                      <span>Owners who want {unitLabel}-level clarity</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className={`h-5 w-5 ${primaryIconText} mr-2 flex-shrink-0 mt-0.5`} />
                      <span>{sectionEntityTitle}s needing compliance tracking</span>
                    </li>
                  </ul>
                </div>
                <div className={`bg-white p-6 rounded-lg border-2 ${accentBorder} shadow-md flex-1 flex flex-col min-h-0`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Brain className={`h-5 w-5 ${primaryIconText} flex-shrink-0`} />
                    <h3 className="text-xl font-bold text-wood-900">Meet Grapia</h3>
                  </div>
                  <p className="text-wood-800 text-sm leading-relaxed">
                    Grapia is your in-app field assistant for {domainLabel} operations. Ask questions about labor, {unitLabelPlural}, compliance, and scouting; get answers grounded in your own data. Available anytime from the chat icon—no extra setup.
                  </p>
                </div>
              </div>
            </div>
            {/* Simple, Predictable Pricing */}
            <div className="flex flex-col min-h-0">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-wood-900 mb-6 text-center lg:text-left">
                Simple, Predictable Pricing
              </h2>
              <div className={`bg-white p-6 rounded-lg border-2 ${accentBorder} shadow-md flex-1 flex flex-col min-h-0`}>
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-wood-900">$125<span className="text-xl font-normal text-wood-700">/month</span></div>
                  <p className="text-wood-700 mt-2">Base plan includes up to 10 active {unitLabelPlural}</p>
                </div>
                <div className={`border-t ${accentBorder} pt-6 space-y-3 text-wood-800`}>
                  <div className="flex items-start">
                    <CheckCircle2 className={`h-5 w-5 ${primaryIconText} mr-3 flex-shrink-0 mt-0.5`} />
                    <div>
                      <span className="font-semibold">$7 per additional active {unitLabel}</span>
                      <p className="text-sm text-wood-600 mt-1">Only charged for {unitLabelPlural} with logged labor</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className={`h-5 w-5 ${primaryIconText} mr-3 flex-shrink-0 mt-0.5`} />
                    <div>
                      <span className="font-semibold">Maximum $350/month</span>
                      <p className="text-sm text-wood-600 mt-1">Price cap protects larger operations</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className={`h-5 w-5 ${primaryIconText} mr-3 flex-shrink-0 mt-0.5`} />
                    <div>
                      <span className="font-semibold">All features included</span>
                      <p className="text-sm text-wood-600 mt-1">Labor tracking, compliance, scouting, and more</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Operations Design — same look as hero: blurred background, subtle overlay, frosted panel */}
      <section className="relative py-14 sm:py-16 overflow-hidden bg-white">
        {consultingBg && (
          <LandingBackgroundMedia
            fallbackSrc={consultingBg}
            alt={`${sectionEntityTitle} consulting`}
            overlay="subtle"
            lazy={true}
          />
        )}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`frosted-panel p-6 sm:p-8 ${isGreenhouse ? 'frosted-panel-green' : ''}`}>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-wood-900 mb-3 text-center">
              Operations Design for Your {sectionEntityTitle}
            </h2>
            <p className="text-wood-700 text-center mb-6 max-w-xl mx-auto">
              Build repeatable field workflows for your {unitLabelPlural} — you stay in control, we streamline the routine.
            </p>
            <ul className="flex flex-wrap justify-center gap-x-6 gap-y-1 mb-8 text-wood-700 text-sm">
              <li className="flex items-center">
                <CheckCircle2 className={`h-4 w-4 ${primaryIconText} mr-1.5 flex-shrink-0`} />
                Map scouting to task workflows crews will use
              </li>
              <li className="flex items-center">
                <CheckCircle2 className={`h-4 w-4 ${primaryIconText} mr-1.5 flex-shrink-0`} />
                Define compliance steps that reduce paperwork
              </li>
              <li className="flex items-center">
                <CheckCircle2 className={`h-4 w-4 ${primaryIconText} mr-1.5 flex-shrink-0`} />
                Connect field observations with {appDataLabel} data
              </li>
              <li className="flex items-center">
                <CheckCircle2 className={`h-4 w-4 ${primaryIconText} mr-1.5 flex-shrink-0`} />
                Pilot, train crews, and scale with clear SOPs
              </li>
            </ul>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mb-6">
              <div className={`flex items-center gap-3 bg-white/60 backdrop-blur-sm rounded-md border ${accentBorder}/80 px-3 py-2.5 min-w-0`}>
                <Video className={`h-5 w-5 ${primaryIconText} flex-shrink-0`} />
                <div className="min-w-0">
                  <span className="text-sm font-bold text-wood-900">Step 1: Video consult</span>
                  <p className="text-wood-600 text-xs mt-0.5">45–60 min, $100: your {unitLabelPlural}, goals, and a design that fits.</p>
                </div>
              </div>
              <div className={`flex items-center gap-3 bg-white/60 backdrop-blur-sm rounded-md border ${accentBorder} px-3 py-2.5 min-w-0`}>
                <MapPin className="h-5 w-5 text-wood-600 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="text-sm font-bold text-wood-900">Step 2: On-site</span>
                  <p className="text-wood-600 text-xs mt-0.5">Design, training, implementation. Quote after the video consult.</p>
                </div>
              </div>
            </div>
            <div className="text-center">
              <button
                type="button"
                onClick={() => setConsultingModalOpen(true)}
                className={`inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded text-vineyard-white ${finalCtaButton} shadow-md transition-colors`}
              >
                Request video consult
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
          <ConsultingRequestModal
            isOpen={consultingModalOpen}
            onClose={() => setConsultingModalOpen(false)}
          />
        </div>
      </section>

      <LandingFinalCtaSection
        vertical={vertical}
        finalCtaBg={finalCtaBg}
        unitLabel={unitLabel}
        domainLabel={domainLabel}
      />

      <LandingFooterSection
        vertical={vertical}
        footerClass={footerClass}
        footerTextClass={footerTextClass}
        footerMutedClass={footerMutedClass}
        headerBrandName={headerBrandName}
        headerLogoSrc={headerLogoSrc}
        isGreenhouse={isGreenhouse}
      />
    </div>
  );
};

export default Landing;
